import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import useToast from '@/components/Toast/useToast';
import { Button, Input, Text, Tooltip } from '@/design-system';
import { xCount } from '@/utils/stringUtils';

import { CONFIG_SETTINGS_LABELS } from '../../constants';
import { useValidateForm } from '../../hooks/useValidateForm';
import { InsightsJobConfig, InsightsJobFormData } from '../../types';
import { convertFormDataToConfig } from '../../utils/formatUtils';
import { useUpdateInsightsJobConfig } from './hooks/useInsightsJobConfigs';

export const SaveDialog = ({
  isOpen,
  onOpenChange,
  formData,
  setErrors,
  diffKeys,
  selectedConfig,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData?: InsightsJobFormData;
  setErrors?: (
    errors: Partial<Record<keyof InsightsJobFormData, string>>
  ) => void;
  diffKeys: string[];
  selectedConfig?: InsightsJobConfig;
}) => {
  const { createToast } = useToast();
  const { validateForm } = useValidateForm({
    formData,
    setErrors,
  });
  const { sessionId } = useParams();

  const onClose = () => {
    setConfigName('');
    onOpenChange(false);
  };

  const [configName, setConfigName] = useState(selectedConfig?.name ?? '');
  const [configDescription, setConfigDescription] = useState(
    selectedConfig?.description ?? ''
  );

  const saveDisabledText = () => {
    if (configName.trim().length === 0) {
      return 'Name cannot be empty';
    }

    if (
      configName == selectedConfig?.name &&
      ((!configDescription && !selectedConfig?.description) ||
        configDescription == selectedConfig?.description) &&
      diffKeys.length === 0
    ) {
      return 'No changes to save';
    }

    return undefined;
  };

  useEffect(() => {
    setConfigName(selectedConfig?.name ?? '');
    setConfigDescription(selectedConfig?.description ?? '');
  }, [selectedConfig]);

  const diffKeysLabels = diffKeys
    ? [...new Set(diffKeys.map((key) => CONFIG_SETTINGS_LABELS[key]))]
    : [];

  const {
    trigger: updateInsightsJobConfig,
    isMutating: isUpdatingInsightsJobConfig,
  } = useUpdateInsightsJobConfig({
    sessionId: sessionId ?? '',
    configId: selectedConfig?.id ?? '',
    options: {
      onError: (e) => {
        createToast({
          title: 'Error updating config',
          description: e.message,
          type: 'error',
        });
      },
      onSuccess: () => {
        createToast({
          title: 'Config updated',
          description: 'Config updated successfully',
          type: 'success',
        });
      },
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent title="Update configuration">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (selectedConfig === undefined) {
              createToast({
                title: 'Error saving config',
                description: 'Please select a config to save.',
                type: 'error',
              });
              onClose();
              return;
            }
            if (!validateForm()) {
              createToast({
                title: 'Config is invalid',
                description: 'Please fix the errors before saving.',
                type: 'error',
              });
              onClose();
            }
            const updatedPayload = {
              name:
                configName !== selectedConfig.name && configName
                  ? configName
                  : undefined,
              description:
                configDescription !== selectedConfig.description &&
                configDescription
                  ? configDescription
                  : undefined,
              config: formData ? convertFormDataToConfig(formData) : undefined,
            };
            await updateInsightsJobConfig({ json: updatedPayload });
            onClose();
          }}
          className="flex flex-col gap-3"
        >
          <Input
            label="Configuration name"
            placeholder="Name"
            value={configName}
            onChange={(value) => setConfigName(value)}
          />
          <Input
            label="Description"
            placeholder="Description"
            value={configDescription}
            onChange={(value) => setConfigDescription(value)}
          />
          {diffKeysLabels && diffKeysLabels.length > 0 && (
            <>
              <Text variant="sm" weight="medium">
                {`Updating ${xCount('field', diffKeysLabels.length)}`}
              </Text>
              <div className="ml-4 flex flex-col gap-2">
                {diffKeysLabels.map((label) => (
                  <Text key={label} variant="xs" weight="medium">
                    {label}
                  </Text>
                ))}
              </div>
            </>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outlined"
              color="secondary"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Tooltip title={saveDisabledText()}>
              <Button
                color="primary"
                size="sm"
                type="submit"
                loading={isUpdatingInsightsJobConfig}
                disabled={!!saveDisabledText()}
              >
                Save
              </Button>
            </Tooltip>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
